/* ============================================================
   Cart-add duplicate-POST guard (Joe/Claude, Aug 2026).

   BUG: clicking the Essential PreOrder app's "PREORDER" button once
   fires TWO identical POST /cart/add.js requests, doubling the
   quantity added (confirmed via captured JS call stacks — both
   requests trace entirely through SlideCart HQ's own compiled
   script, slidecarthq.js, via two separate click listeners it
   attaches to that one button). Normal in-stock "Add to cart"
   buttons are unaffected — only one listener matches them.

   This isn't something we can fix by editing SlideCart's or
   Essential PreOrder's own hosted code (neither lives in this
   theme), and a ticket is in with SlideCart HQ to fix the double
   binding at the source. This script is a stopgap that fixes the
   symptom from our side in the meantime: it makes a genuine
   duplicate POST /cart/add.js (identical body, fired within a very
   short window of a prior one) a no-op, while still resolving the
   caller's request/promise with the FIRST call's real response —
   so whichever app issued the second call still sees a normal
   success response and won't error or hang its own UI.

   Deliberately narrow to avoid false positives: only collapses a
   second POST when the body is byte-for-byte identical to one
   already in flight within DEDUPE_WINDOW_MS. A shopper clicking
   "Add to cart" again a second or two later to genuinely add a
   second unit falls outside that window and goes through normally
   — this only catches the near-simultaneous, same-tick duplicate
   this specific bug produces.

   Covers both XMLHttpRequest (how SlideCart issues the call today)
   and fetch (in case that changes), since neither app's future
   updates are anything we control.

   Remove this file (and its <script> include in theme.liquid) once
   SlideCart HQ confirms the double-binding is fixed on their end —
   this doc's ticket status: see project doc
   "pre-order-double-add-to-cart-bug.md".
   ============================================================ */
(function () {
  if (window.__gtCartAddDedupeLoaded) return;
  window.__gtCartAddDedupeLoaded = true;

  var DEDUPE_WINDOW_MS = 800;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  function isCartAddPost(url, method) {
    return CART_ADD_RE.test(url || '') && String(method || 'GET').toUpperCase() === 'POST';
  }

  function bodyKey(body) {
    if (body == null) return '';
    if (typeof body === 'string') return body;
    try { return JSON.stringify(body); } catch (e) { return String(body); }
  }

  function warn(msg) {
    try { if (window.console && console.warn) console.warn('[GT cart-add guard] ' + msg); } catch (e) {}
  }

  // ---- XMLHttpRequest ----
  var NativeXHR = window.XMLHttpRequest;
  var pendingXhr = null; // { key, time, promise }

  function GtXHR() {
    var xhr = new NativeXHR();
    var _url, _method, _isAdd = false;

    var nativeOpen = xhr.open;
    xhr.open = function (method, url) {
      _method = method;
      _url = url;
      _isAdd = isCartAddPost(url, method);
      return nativeOpen.apply(xhr, arguments);
    };

    var nativeSend = xhr.send;
    xhr.send = function (body) {
      if (!_isAdd) return nativeSend.apply(xhr, arguments);

      var key = bodyKey(body);
      var now = Date.now();

      if (pendingXhr && pendingXhr.key === key && (now - pendingXhr.time) < DEDUPE_WINDOW_MS) {
        warn('suppressed duplicate POST ' + _url + ' (identical payload within ' + DEDUPE_WINDOW_MS + 'ms) — mirroring the original request\'s response instead of sending a second one');
        pendingXhr.promise.then(function (result) {
          try {
            Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
            Object.defineProperty(xhr, 'status', { value: result.status, configurable: true });
            Object.defineProperty(xhr, 'statusText', { value: result.statusText, configurable: true });
            Object.defineProperty(xhr, 'responseText', { value: result.responseText, configurable: true });
            Object.defineProperty(xhr, 'response', { value: result.responseText, configurable: true });
          } catch (e) { /* if any property can't be redefined, still fire the events below */ }
          if (typeof xhr.onreadystatechange === 'function') { try { xhr.onreadystatechange(); } catch (e) {} }
          if (typeof xhr.onload === 'function') { try { xhr.onload(); } catch (e) {} }
          try { xhr.dispatchEvent(new Event('readystatechange')); } catch (e) {}
          try { xhr.dispatchEvent(new Event('load')); } catch (e) {}
          try { xhr.dispatchEvent(new Event('loadend')); } catch (e) {}
        });
        return; // do NOT call nativeSend — no second network request goes out
      }

      var resolvePromise;
      var promise = new Promise(function (res) { resolvePromise = res; });
      pendingXhr = { key: key, time: now, promise: promise };
      xhr.addEventListener('loadend', function () {
        resolvePromise({ status: xhr.status, statusText: xhr.statusText, responseText: xhr.responseText });
      });

      return nativeSend.apply(xhr, arguments);
    };

    return xhr;
  }
  GtXHR.prototype = NativeXHR.prototype;
  window.XMLHttpRequest = GtXHR;

  // ---- fetch ----
  var nativeFetch = window.fetch;
  var pendingFetch = null; // { key, time, promise }
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (typeof input === 'object' && input && input.method) || 'GET';
      if (!isCartAddPost(url, method)) return nativeFetch.apply(this, arguments);

      var key = bodyKey(init && init.body);
      var now = Date.now();

      if (pendingFetch && pendingFetch.key === key && (now - pendingFetch.time) < DEDUPE_WINDOW_MS) {
        warn('suppressed duplicate fetch ' + url + ' (identical payload within ' + DEDUPE_WINDOW_MS + 'ms) — mirroring the original request\'s response instead of sending a second one');
        return pendingFetch.promise.then(function (res) { return res.clone(); });
      }

      var p = nativeFetch.apply(this, arguments);
      pendingFetch = { key: key, time: now, promise: p };
      return p;
    };
  }
})();
